import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Button,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { REGEX } from "constants/CommonConstants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { getAdminById, addAdmin, updateAdminById } from "action/admin";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

const initialFields = {
  firstName: {
    value: "",
    originalValue: "",
    error: "",
  },
  lastName: {
    value: "",
    originalValue: "",
    error: "",
  },
  email: {
    value: "",
    originalValue: "",
    error: "",
  },
  mobile: {
    value: "",
    originalValue: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    originalValue: "ACTIVE",
    error: "",
  },
};

function AddAdmin() {
  const { notify } = useContext(NotificationContext);
  const { id: encodedId } = useParams();
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || "add";
  const decodedId =
    encodedId && mode === "edit" ? urlDecodedId(encodedId) : null;
  const [formData, setFormData] = useState(initialFields);
  const [isSaving, setIsSaving] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  const fetchAdminData = async (id) => {
    try {
      setShowAlternative({ loader: true, error: "" });

      const res = await getAdminById(id);

      if (res?.code === 200) {
        setFormData({
          firstName: {
            value: res.data.firstName || "",
            originalValue: res.data.firstName || "",
            error: "",
          },
          lastName: {
            value: res.data.lastName || "",
            originalValue: res.data.lastName || "",
            error: "",
          },
          email: {
            value: res.data.email || "",
            originalValue: res.data.email || "",
            error: "",
          },
          mobile: {
            value: res.data.mobile || "",
            originalValue: res.data.mobile || "",
            error: "",
          },
          status: {
            value: res.data.status || "ACTIVE",
            originalValue: res.data.status || "ACTIVE",
            error: "",
          },
        });
        setShowAlternative({ loader: false, error: "" });
      } else {
        setShowAlternative({ loader: false, error: "" });
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to load Admin details!";
        notify("danger", message);
      }
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: error?.message || "Something went wrong while fetching admin",
      });
    }
  };

  // Prefill form when in edit mode
  useEffect(() => {
    if (mode !== "edit" || !decodedId) return;

    fetchAdminData(decodedId);
  }, [mode, decodedId]);

  const handleInputChange = (field, value) => {
    if (field === "mobile") {
      if (!REGEX.ONLY_DIGITS_REGEX.test(value)) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "firstName":
        return !trimmedValue
          ? "Please enter first name"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(value)
            ? "First name must contain only letters and spaces"
            : "";

      case "lastName":
        return !trimmedValue
          ? "Please enter last name"
          : !REGEX.ONLY_ALPHABETS_REGEX.test(value)
            ? "Last name must contain only letters and spaces"
            : "";

      case "email":
        return !trimmedValue
          ? "Please enter email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid email"
            : "";

      case "mobile":
        return !value
          ? "Please enter mobile number"
          : value.length !== 10
            ? "Mobile number must be 10 digits"
            : !REGEX.PHONE_NO_REGEX.test(value)
              ? "Please enter a valid mobile number"
              : "";

      default:
        return "";
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newAdmin = { ...formData };

    //get all the keys in array
    Object.keys(newAdmin).forEach((key) => {
      newAdmin[key] = {
        ...newAdmin[key],
        error: getValidationError(key, newAdmin[key].value),
      };
    });
    setFormData(newAdmin); //if error set error state
    return Object.values(newAdmin).every((item) => item.error === "");
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSaving(true);
      let res;

      if (decodedId) {
        const payload = {};

        Object.keys(formData).forEach((key) => {
          if (formData[key].value !== formData[key].originalValue) {
            payload[key] = formData[key].value;
          }
        });

        if (!Object.keys(payload).length) {
          notify("info", "No changes detected");
          setIsSaving(false);
          return;
        }

        res = await updateAdminById(decodedId, payload);
      } else {
        const payload = {
          firstName: formData.firstName.value,
          lastName: formData.lastName.value,
          email: formData.email.value,
          mobile: formData.mobile.value,
          status: formData.status.value,
        };

        res = await addAdmin(payload);
      }

      if (res?.code === 200) {
        notify(
          "success",
          res?.message ||
            (decodedId
              ? "Admin updated successfully!"
              : "Admin added successfully!"),
        );

        navigate(role === "SUPER_ADMIN" ? "/super-admin/admin" : "/admin/list");
      } else {
        notify(
          "danger",
          res?.message || `Failed to ${decodedId ? "update" : "add"} admin`,
        );
      }
    } catch (error) {
      notify("danger", error?.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOnCancel = () => {
    setFormData(initialFields);
    navigate(role === "SUPER_ADMIN" ? "/super-admin/admin" : "/admin/list");
  };
  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row className="justify-content-center">
          <Col>
            <div
              className="d-flex align-items-center cursor text-white"
              onClick={() =>
                navigate(
                  role === "SUPER_ADMIN" ? "/super-admin/admin" : "/admin/list",
                )
              }
            >
              <i className="fa-solid fa-angle-left mr-2" />
              <span className="font-weight-bold">Back</span>
            </div>
            <Card className="mb-3 mt-3">
              <CardHeader className="d-flex align-items-center">
                <h3 className="mb-0 section-header-title">
                  {mode === "edit" ? "Edit Admin" : "Add Admin"}
                </h3>
              </CardHeader>

              <CardBody>
                {mode === "edit" && showAlternative.loader ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : showAlternative.error ? (
                  <div className="loaderContainer text-muted d-flex flex-column">
                    <h4 className="text-muted">No Admin Data Available</h4>
                    <span className="text-danger">{showAlternative.error}</span>
                  </div>
                ) : (
                  <>
                    <Form>
                      <Row>
                        <Col md="4" sm="12">
                          <FormGroup>
                            <Label className="form-control-label">
                              First Name
                            </Label>
                            <span className="text-danger"> *</span>
                            <Input
                              type="text"
                              name="firstName"
                              placeholder="Enter first name"
                              maxLength={50}
                              value={formData.firstName.value}
                              onChange={(e) =>
                                handleInputChange("firstName", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                formData.firstName.error ? "is-invalid" : ""
                              }
                            />
                            {formData.firstName.error && (
                              <div className="invalid-feedback d-block">
                                {formData.firstName.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4" sm="12">
                          <FormGroup>
                            <Label className="form-control-label">
                              Last Name
                            </Label>
                            <span className="text-danger"> *</span>
                            <Input
                              type="text"
                              name="lastName"
                              placeholder="Enter last name"
                              maxLength={50}
                              value={formData.lastName.value}
                              onChange={(e) =>
                                handleInputChange("lastName", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                formData.lastName.error ? "is-invalid" : ""
                              }
                            />
                            {formData.lastName.error && (
                              <div className="invalid-feedback d-block">
                                {formData.lastName.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4" sm="12">
                          <FormGroup>
                            <Label className="form-control-label">Email</Label>
                            <span className="text-danger"> *</span>
                            <Input
                              type="email"
                              name="email"
                              placeholder="Enter email"
                              maxLength={100}
                              value={formData.email.value}
                              onChange={(e) =>
                                handleInputChange("email", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                formData.email.error ? "is-invalid" : ""
                              }
                            />
                            {formData.email.error && (
                              <div className="invalid-feedback d-block">
                                {formData.email.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4" sm="12">
                          <FormGroup>
                            <Label className="form-control-label">
                              Mobile Number
                            </Label>
                            <span className="text-danger"> *</span>
                            <Input
                              name="mobile"
                              placeholder="Enter mobile number"
                              value={formData.mobile.value}
                              onChange={(e) =>
                                handleInputChange("mobile", e.target.value)
                              }
                              onBlur={(e) =>
                                handleOnBlur(e.target.name, e.target.value)
                              }
                              className={
                                formData.mobile.error ? "is-invalid" : ""
                              }
                              maxLength="10"
                            />
                            {formData.mobile.error && (
                              <div className="invalid-feedback d-block">
                                {formData.mobile.error}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md="4" sm="12">
                          <FormGroup>
                            <Label className="form-control-label">
                              Status <span className="text-danger"> *</span>
                            </Label>

                            <div className="d-flex mt-2">
                              <FormGroup check className="mr-4">
                                <Label check>
                                  <Input
                                    type="radio"
                                    name="status"
                                    className="cursor"
                                    checked={formData.status.value === "ACTIVE"}
                                    onChange={() =>
                                      handleInputChange("status", "ACTIVE")
                                    }
                                  />
                                  Active
                                </Label>
                              </FormGroup>

                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="radio"
                                    name="status"
                                    className="cursor"
                                    checked={
                                      formData.status.value === "INACTIVE"
                                    }
                                    onChange={() =>
                                      handleInputChange("status", "INACTIVE")
                                    }
                                  />
                                  Inactive
                                </Label>
                              </FormGroup>
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <Col className="text-right">
                          <Button
                            color="secondary"
                            className="mr-2 save-btn-sm"
                            type="button"
                            onClick={handleOnCancel}
                          >
                            Cancel
                          </Button>

                          <Button
                            color="primary"
                            className="btn-deep-blue mr-2 save-btn-sm"
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                          >
                            {isSaving ? (
                              <>
                                <i className="fa fa-spinner fa-spin fa-lg text-white" />
                              </>
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default AddAdmin;
